import React, { useState, useEffect, useRef, useLayoutEffect } from "react";
import { Container, Row, Col, Form } from "react-bootstrap";
import Plot from "react-plotly.js";
import { userService, analyticsService } from "../../services/api";
import '../../styles/components/features/analytics.css';

export default function StudentAnalytics() {
    const [studentUsernames, setStudentUsernames] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState("");
    const [studentErrors, setStudentErrors] = useState([]);
    const [studentStatistics, setStudentStatistics] = useState(null);
    const measureRef = useRef(null);
    const [selectWidth, setSelectWidth] = useState("auto");

    const fetchStudentUsernames = async () => {
        try {
            const studentUsernames = await userService.getUsernames();
            setStudentUsernames(studentUsernames);
        } catch (error) {
            console.error("Error fetching student usernames:", error);
        }
    };

    const fetchStudentIdFromUsername = async (username) => {
        try {
            const studentData = await userService.getStudentIdFromUsername(username);
            return studentData.studentID;
        } catch (error) {
            console.error("Error fetching student ID from username:", error);
        }
    };

    const fetchErrorsPerStudent = async (studentId) => {
        try {
            const errorsData = await analyticsService.errorsPerStudent(studentId);
            setStudentErrors(errorsData);
        } catch (error) {
            console.error("Error fetching errors per student:", error);
        }
    };

    const fetchStudentIndividualStatistics = async (studentId) => {
        try {
            const statisticsData = await analyticsService.studentIndividualStatistics(studentId);
            console.log("Fetched Student Statistics:", statisticsData); // Log the fetched statistics
            setStudentStatistics(statisticsData[0]); // Assuming the backend returns an array
        } catch (error) {
            console.error("Error fetching individual student statistics:", error);
        }
    };

    useEffect(() => {
        fetchStudentUsernames();
    }, []);

    // Measure the longest username and size the select to fit its content
    useLayoutEffect(() => {
        if (!studentUsernames || studentUsernames.length === 0) {
            setSelectWidth("auto");
            return;
        }
        const longest = studentUsernames.reduce((a, b) => (a.length >= b.length ? a : b), "");
        if (measureRef.current) {
            measureRef.current.textContent = longest || "";
            // Add some extra space for the caret and padding
            const base = measureRef.current.offsetWidth || 0;
            const extra = 48; // caret + padding buffer
            setSelectWidth(`${base + extra}px`);
        }
    }, [studentUsernames]);

    const handleStudentSelection = async (e) => {
        const studentUsername = e.target.value;
        setSelectedStudent(studentUsername);
    
        const studentId = await fetchStudentIdFromUsername(studentUsername);
        console.log("Fetched Student ID:", studentId); // Log the fetched student ID
        if (studentId) {
            await fetchErrorsPerStudent(studentId);
            await fetchStudentIndividualStatistics(studentId);
        }
    };

    return (
        <Container className="analytics-container">
            <div className="analytics-title">
                <h1>Real-Time Student Performance Dashboard</h1>
            </div>
            <Form.Group controlId="studentSelect" className="analytics-form-group">
                <Form.Label className="analytics-label analytics-label-lg">Select a Student</Form.Label>
                <span ref={measureRef} className="analytics-measure" />
                <Form.Select
                    className="analytics-select"
                    style={{ width: selectWidth }}
                    value={selectedStudent}
                    onChange={handleStudentSelection}
                >
                    <option value="">-- Select a Student --</option>
                    {studentUsernames.map((username, index) => (
                        <option key={index} value={username}>
                            {username}
                        </option>
                    ))}
                </Form.Select>
            </Form.Group>

            {studentStatistics && (
                <Row className="mb-4">
                    <Col md={4} className="text-center">
                        <h5>Total Distinct Topics</h5>
                        <p>{studentStatistics.totalDistinctTopics ?? "N/A"}</p>
                    </Col>
                    <Col md={4} className="text-center">
                        <h5>Most Incorrect Topic</h5>
                        <p>{studentStatistics.mostErrorTopic ?? "N/A"}</p>
                    </Col>
                    <Col md={4} className="text-center">
                        <h5>Submissions Last Week</h5>
                        <p>{studentStatistics.submissionsLastWeek ?? "N/A"}</p>
                    </Col>
                </Row>
            )}

            {studentErrors.length > 0 && (
                <>
                    <h3 className="text-center mb-4">Errors Per Student</h3>
                    <div className="text-center">
                        <Plot
                            data={[
                                {
                                    x: studentErrors.map((error) => error.errorType),
                                    y: studentErrors.map((error) => error.errorCount),
                                    type: "bar",
                                },
                            ]}
                            layout={{
                                title: "Errors Per Student",
                                xaxis: { title: "Error Type" },
                                yaxis: { title: "Error Count" },
                            }}
                        />
                    </div>
                </>
            )}
        </Container>
    );
}